export type LngLat = [number, number];

export function lineFeatureCollection(coords: LngLat[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: {},
      },
    ],
  };
}

/** Mapbox driving directions between ordered waypoints [lng, lat]. */
export async function fetchMapboxDrivingRoute(
  waypoints: LngLat[],
  accessToken: string,
): Promise<LngLat[]> {
  if (waypoints.length < 2 || !accessToken) return [];
  const coordsStr = waypoints.map(([lon, lat]) => `${lon},${lat}`).join(';');
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}` +
    `?geometries=geojson&overview=full&steps=false&access_token=${accessToken}`;
  const res = await fetch(url);
  const json = await res.json();
  return (json?.routes?.[0]?.geometry?.coordinates ?? []) as LngLat[];
}

/** Same request as `fetchMapboxDrivingRoute`, also surfacing the route's ETA. */
export async function fetchMapboxDrivingSummary(
  waypoints: LngLat[],
  accessToken: string,
): Promise<{ coords: LngLat[]; durationMinutes: number | null }> {
  if (waypoints.length < 2 || !accessToken) return { coords: [], durationMinutes: null };
  const coordsStr = waypoints.map(([lon, lat]) => `${lon},${lat}`).join(';');
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/driving/${coordsStr}` +
    `?geometries=geojson&overview=full&steps=false&access_token=${accessToken}`;
  const res = await fetch(url);
  const json = await res.json();
  const route = json?.routes?.[0];
  const durationSeconds = typeof route?.duration === 'number' ? route.duration : null;
  return {
    coords: (route?.geometry?.coordinates ?? []) as LngLat[],
    durationMinutes: durationSeconds != null ? Math.round(durationSeconds / 60) : null,
  };
}

/**
 * Mapbox walking directions between ordered waypoints [lng, lat]. This is
 * the regular outdoor pedestrian profile (sidewalks/paths in Mapbox's road
 * graph) — it does not know about a building's interior corridors, only
 * ways that exist in the map data. Returns an empty array if no route can
 * be found (common when one end is inside a building with no mapped path),
 * so callers should fall back to a straight line in that case.
 */
export async function fetchMapboxWalkingRoute(
  waypoints: LngLat[],
  accessToken: string,
): Promise<LngLat[]> {
  if (waypoints.length < 2 || !accessToken) return [];
  const coordsStr = waypoints.map(([lon, lat]) => `${lon},${lat}`).join(';');
  const url =
    `https://api.mapbox.com/directions/v5/mapbox/walking/${coordsStr}` +
    `?geometries=geojson&overview=full&steps=false&access_token=${accessToken}`;
  const res = await fetch(url);
  const json = await res.json();
  return (json?.routes?.[0]?.geometry?.coordinates ?? []) as LngLat[];
}

/** ~meters between two WGS84 points (rough haversine). */
export function distanceMeters(a: LngLat, b: LngLat): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
